import Storage from 'storage';
import QuizDB, { fetchQuizLocal } from 'quizdb';
import cloneDeep from "lodash.clonedeep";
import converter from 'number-to-words';
import { message } from "antd";
import ImagePreloader from 'ImagePreloader';

function __clearQuestions() {
  sessionStorage.removeItem('questions');
}

function clearResponses() {
  sessionStorage.removeItem('responses');
}

function saveJSONToLocal(key, data) {
  sessionStorage.setItem(key, JSON.stringify(data));
}

function fetchJSONFromLocal(key) {
  const item = sessionStorage.getItem(key);
  if (item !== undefined & item !== "undefined") return JSON.parse(item);
  return item;
}

const quizNames = [
  'China Quiz', 'Placement Test',
]
function silkMandarinQuiz() {
  const currentQuizName = getQuizName();
  return quizNames.find(quizName => currentQuizName.includes(quizName));
}

function saveQuestionsLocal(questions) {
  saveJSONToLocal('questions', questions);
}

function pushQuestionsDB(onSuccess=()=>{}, onError=()=>{}) {
  const questions = fetchQuestions();
  QuizDB.saveQuestions(getQuizName(), questions, onSuccess, onError)
}

function saveQuestion(quizName, question, index=0) {
  /*
    in composer mode, any change to questions and we need to sync
    responses. easier to just invalidate the whole array and start
    from scratch in saveResponseLocal()
  */
  clearResponses()

  const questions = fetchQuestions();
  questions[index] = question;
  saveJSONToLocal('questions', questions);
}

function fetchQuestions(index) {
  const questions = JSON.parse(sessionStorage.getItem('questions')) || {};
  if (index >= 0) {
    return questions[index];
  }
  return questions
}

function fetchResponses(questionName) {
  const responses = JSON.parse(sessionStorage.getItem('responses')) || {};
  if (questionName) {
    return responses[questionName];
  }
  return responses
}

function __cloneQuestionTool(id, index=0) {
  const questions = fetchQuestions(index);
  return cloneDeep(questions.find(tool => {
    return 'response' in tool && tool.id === id;
  }));
}

function __cloneResponseTool(id, questionName) {
  const responses = fetchResponses(questionName);
  return cloneDeep(responses.find(tool => {
    return 'response' in tool && tool.id === id;
  }));
}

function _updateToolResponse(tool, response) {
  if (!tool) return false;
  tool.response = response;
  return tool
}

function _updateResponse(questionName, toolUpdated) {
  const updatedResponse = cloneDeep(fetchResponses(questionName));
  const matchingToolIndex = updatedResponse.
  findIndex(tool => tool.id === toolUpdated.id);
  updatedResponse[matchingToolIndex] = toolUpdated;
  return updatedResponse
}

function _updateResponses(questionName, responseUpdated) {
  const responsesUpdated = cloneDeep(fetchResponses());
  responsesUpdated[questionName] = responseUpdated;
  return responsesUpdated
}

function saveResponseLocal(id, response, quizName, questionId, onSuccess=()=>{}, onError=()=>{}) {
  saveSummaryResponseLocal(id, response, quizName, questionId, onSuccess=()=>{}, onError=()=>{});
}

function saveSummaryResponseLocal(id, response, quizName, questionId, onSuccess=()=>{}, onError=()=>{}) {
  const responses = JSON.parse(sessionStorage.getItem('responses')) || {};
  responses[questionId] = response;

  saveJSONToLocal('responses', responses);
  Subscribe.callOnSaveResponseCallbacks();
}

/*
  We want to create a snapshot of the quiz at the top it was taken,
  so we record the entire quiz structure. But this takes up alot
  of storage space and storage is a higher priority than fidelity.

  Should probably enforce unique quiz & question names and inform users
  that saved responses refer to the current quiz & question names. So 
  changing quiz & question names make make it difficult to recover
  which responses belong to which quiz & question.

  We hope to reuse the below code in the future but, for now its
  lights out.
*/
function saveCompleteResponseLocal(id, response, quizName, questionName, onSuccess=()=>{}, onError=()=>{}) {
  const responses = JSON.parse(sessionStorage.getItem('responses')) || {};
  const responsesHasNoKeys = Object.values(responses).length === 0;
  if (responsesHasNoKeys) {
    const quizClone = cloneDeep(fetchQuizLocal())
    saveJSONToLocal('responses', quizClone);
  }

  const toolClone = __cloneResponseTool(id, questionName)
  const toolUpdated = _updateToolResponse(toolClone, response)
  const updatedResponseComplete = _updateResponse(questionName, toolUpdated)
  const UpdatedResponseSummary = toolUpdated.response;
  const responsesUpdated = _updateResponses(questionName, UpdatedResponseSummary)

  saveJSONToLocal('responses', responsesUpdated);
  Subscribe.callOnSaveResponseCallbacks();
}

function pushResponseDB(onSuccess, onError) {
  QuizDB.saveStudentResponse(getStudentInfo(), getQuizId(),
                             fetchResponses(), onSuccess, onError);
}

function saveStudentInfo(name, email) {
  const studentInfo = {name, email}
  sessionStorage.setItem('student_info', JSON.stringify(studentInfo));
}

function saveJSONToFile(data, filename) {
  const blob = new Blob(JSON.stringify([data]), {type: "application/json;charset=utf-8"});
  const url = URL.createObjectURL(blob);
  const elem = document.createElement("a");
  elem.href = url;
  elem.download = filename;
  document.body.appendChild(elem);
  elem.click();
  document.body.removeChild(elem);
  console.log('saving data to file', data);
}

function isActive(activeButton) {
  return activeButton > -1
}

class Subscribe {
  static onSaveResponses = []

  static onSaveResponse(questionsIndex, callback) {
    Subscribe.onSaveResponses.push(callback)
  }

  static callOnSaveResponseCallbacks() {
    Subscribe.onSaveResponses.forEach((callback) => callback())
  }
}

function deleteQuestion(index) {
  const questions = fetchQuestions();
  delete questions[index];
  saveJSONToLocal('questions', questions)
}

function quizHasQuestionsRemaining(selectedQuestion: number) {
  return selectedQuestion >= 0;
}

function questionsNotEmpty(questions) {
  return Object.keys(questions).length > 0
}

function getFirstQuestionIndex(questions) {
  if (Object.keys(questions).length === 0) return -1
  return Number(Object.keys(questions)[0]);
}

function hasQuestion(selectedQuestion, questions) {
  return selectedQuestion in questions;
}

function getAdjacentQuestionIndex(selectedQuestion, questions) {
  const keys = Object.keys(questions);
  const indexOfSelectedQuestion = keys.indexOf(String(selectedQuestion));
  const indexOfPrevQ = keys[indexOfSelectedQuestion-1];
  if (hasQuestion(indexOfPrevQ, questions)) {
    return Number(indexOfPrevQ);
  }
  const indexOfNextQ = keys[indexOfSelectedQuestion+1];
  return Number(indexOfNextQ);
}

function getPushedQuestionIndex(questions) {
  const keys = Object.keys(questions);
  const lastKeyIndex = keys.length-1
  const lastQuestionIndex = Number(keys[lastKeyIndex]);
  const pushedQuestionIndex = Number(keys[lastKeyIndex]) + 1;
  return pushedQuestionIndex
}

function setQuizName(quizName) {
  sessionStorage.setItem('quiz_name', quizName);
}

function getQuizName() {
  return sessionStorage.getItem('quiz_name')
}

function clearQuizName() {
  return sessionStorage.removeItem('quiz_name')
}

function setQuestionName(questionName) {
  sessionStorage.setItem('question_name', questionName);
}

function getQuestionName() {
  return sessionStorage.getItem('question_name')
}

function clearQuestionName() {
  return sessionStorage.removeItem('question_name')
}

function setQuizId(quizId) {
  sessionStorage.setItem('quiz_id', quizId);
}

function getQuizId() {
  return sessionStorage.getItem('quiz_id')
}

function clearQuizId() {
  return sessionStorage.removeItem('quiz_id')
}

function setQuestionId(questionId) {
  sessionStorage.setItem('question_id', questionId);
}

function getQuestionId() {
  return sessionStorage.getItem('question_id')
}

function clearQuestionId() {
  return sessionStorage.removeItem('question_id')
}

function getStudentInfo() {
  return JSON.parse(sessionStorage.getItem('student_info'));
}

function calcTotalNumQuestions() {
  const questions = fetchQuizLocal(getQuizName());
  return Object.keys(questions).length;
}

function numToWord(num) {
  return converter.toOrdinal(num);
}

function loadQuestions(onSuccess, onError) {
  QuizDB.loadQuestions(onSuccess, onError);
}

function loadResponses(onSuccess, onError) {
  QuizDB.loadResponses(onSuccess, onError);
}

const answerFunctions = {
  MultiChoiceGrid:(tool:any)=>tool.mediaFiles.findIndex((media) => media.is_answer),
  MultiChoiceRow:(tool)=>tool.mediaFiles.findIndex((media) => media.is_answer),
  MultiChoiceCol:(tool)=>tool.mediaFiles.findIndex((media) => media.is_answer),
  FillBlankText:(tool)=>{
    const correctHanziNormal = tool.sentences.correctAnswers.hanzi.reduce(normalizeText).split('/')
    const correctPinyinNormal = tool.sentences.correctAnswers.pinyin.reduce(normalizeText).split('/')
    const correctAnswerNormal = correctHanziNormal.map((hanzi, index) => {
      let answer = hanzi+'/'+correctPinyinNormal[index]
      return (index < correctPinyinNormal.length-1) ? answer+' or ' : answer;
    }).reduce((prev, curr) => prev+curr);
    return correctAnswerNormal;
  },
  MultiLineInput:(tool:any)=>'please manually check this question, an answer not available at this time',
  RearrangeText:(tool:any)=>{
    return tool.disarrangedText.arranged.map((answer:any) => {
      return answer.hanzi + ' or ' + answer.pinyin
    }).reduce((prev:any, curr:any) => {return prev + ' or ' + curr});
  },
  TextSwitcher:(tool)=>'please manually check this question, an answer not available at this time',
  SelectBlankText:(tool)=>'please manually check this question, an answer not available at this time',
  Select:(tool)=>'please manually check this question, an answer not available at this time',
  ImageSlideShow:(tool)=>'please manually check this question, an answer not available at this time',
}

const trimText = (value) => {
  return value.trim().replaceAll(' ', '')
}

const normalizeText = (prev, curr) => {
  if (!prev && curr) {
    return trimText(curr).toLocaleLowerCase();
  } else if (prev && !curr) {
    return trimText(prev).toLocaleLowerCase();
  } else if (!prev && !curr) {
    return '';
  } else if (prev && curr) {
    return trimText(prev).toLocaleLowerCase()
           +'/'+
           trimText(curr).toLocaleLowerCase()
  }
}

const modes = {
  compose: "0",
  render: "1",
  student: "2",
}

const modeNames = {
  '0': 'compose',
  '1': 'render',
  '2': 'student',
}

function getModeName() {
  const mode = sessionStorage.getItem('mode');
  return modeNames[mode];
}

function setModeName(mode) {
  sessionStorage.setItem('mode', mode);
}

const atoz = "a b c d e f g h i j k l m n o p q r s t u v w x y z";
function intToAlpha(number, caps=true) {
  if (caps) return atoz.toUpperCase().split(" ")[number];
  return atoz.split(" ")[number];
}

function getAnswer(question) {
  const tool = question.tools.find((tool:any) => 'response' in tool);
  if (tool) return answerFunctions[tool.element](tool);
}

function getResponseTool(question) {
  return question.tools.find((tool:any) => 'response' in tool);
}

function quizNameToDeploymentUrl(quizName) {
  const quizNameUrlFriendly = quizName.replaceAll(' ', '-');
  const deploymenturl = `https://echolabstech.github.io/quiz-deployment/${quizNameUrlFriendly}/`
  return deploymenturl;
}

function __findCreatedAt(question) {
  const tool = question.tools.find((tool) => tool.createdAt);
  return tool ? tool.createdAt : Date.now();
}

function __byCreatedAt(question1, question2) {
  if (__findCreatedAt(question1) < __findCreatedAt(question2)) return -1;
  if (__findCreatedAt(question1) > __findCreatedAt(question2)) return 1;
  return 0;
}

function __byEntry(entry1, entry2) {
  return __byCreatedAt(entry1[1], entry2[1])
}

function sortQuestionEntries(quiz) {
  return Object.entries(quiz.questions).sort(__byEntry).map((entry)=>entry);
}

function preloadAllMedia(quiz) {
  const questions = sortQuestionEntries(quiz);
  questions.forEach((questionEntry) => {
    Object.entries(questionEntry[1].tools).forEach((toolEntry) => {
      if ('mediaFiles' in toolEntry[1]) {
        toolEntry[1].mediaFiles.forEach((mediaFile, index) => {
          if ('src' in mediaFile) {
            ImagePreloader.preload(mediaFile.src, mediaFile.src);
          }
        });
      }
    });
  });
}

export {
  saveQuestion,
  fetchQuestions,
  saveResponseLocal,
  Subscribe,
  isActive,
  deleteQuestion,
  quizHasQuestionsRemaining,
  questionsNotEmpty,
  getFirstQuestionIndex,
  hasQuestion,
  getAdjacentQuestionIndex,
  getPushedQuestionIndex,
  saveQuestionsLocal,
  setQuizName,
  getQuizName,
  clearQuizName,
  setQuizId,
  getQuizId,
  clearQuizId,
  setQuestionName,
  getQuestionName,
  clearQuestionName,
  setQuestionId,
  getQuestionId,
  clearQuestionId,
  pushResponseDB,
  getStudentInfo,
  calcTotalNumQuestions,
  numToWord,
  pushQuestionsDB,
  saveStudentInfo,
  loadResponses,
  answerFunctions,
  trimText,
  normalizeText,
  loadQuestions,
  saveJSONToLocal,
  fetchJSONFromLocal,
  clearResponses,
  sortQuestionEntries,
  setModeName,
  getModeName,
  modes,
  intToAlpha,
  getAnswer,
  fetchResponses,
  quizNameToDeploymentUrl,
  silkMandarinQuiz,
  getResponseTool,
  preloadAllMedia,
}